export type AssistantExecutionMode = 'bedrock' | 'browser_local' | 'deferred';
export type AssistantPreferredRuntime = 'auto' | 'bedrock' | 'browser_local';

export type AssistantBrowserCapabilities = {
  webgpu?: boolean;
  deviceMemoryGb?: number;
  downlinkMbps?: number;
  effectiveType?: 'slow-2g' | '2g' | '3g' | '4g' | 'unknown';
  hardwareConcurrency?: number;
};

export type AssistantRuntimePreferences = {
  preferredRuntime?: AssistantPreferredRuntime;
  localVisionOptIn?: boolean;
  browser?: AssistantBrowserCapabilities;
};

export type AssistantRuntimePolicyHint = {
  executionMode?: 'shadow' | 'live';
  revision?: number;
  lane?: string | null;
  multimodalGrounding?: {
    visualExtractorModel?: string | null;
    teacherModel?: string | null;
  };
};

export type AssistantExecutionPlan = {
  selectedMode: AssistantExecutionMode;
  recommendedMode: AssistantExecutionMode;
  requiresOptIn: boolean;
  reason: string;
  fallbackReason: string | null;
  estimatedColdStartMs: number | null;
  visualExtractorModel: string | null;
  teacherModel: string | null;
};

type ResolveAssistantExecutionPlanInput = {
  question?: string;
  preferences?: AssistantRuntimePreferences;
  policy?: AssistantRuntimePolicyHint;
  serverSupportsBrowserLocal?: boolean;
};

const MOONDREAM2_Q4_DOWNLOAD_MB = 1252.75;
const DEFAULT_DOWNLINK_MBPS = 35;
const LOCAL_VISION_MIN_DEVICE_MEMORY_GB = 8;
const LOCAL_VISION_MIN_DOWNLINK_MBPS = 20;

const VISION_QUERY_PATTERN = /\b(image|photo|picture|scan|screenshot|ocr|document image|visual|attached file|uploaded file|file upload|attachment|pdf)\b/i;

const asFinitePositive = (value: unknown): number | undefined => {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    return undefined;
  }
  return value;
};

const normalizeEffectiveType = (value: unknown): AssistantBrowserCapabilities['effectiveType'] => {
  if (value === 'slow-2g' || value === '2g' || value === '3g' || value === '4g') {
    return value;
  }
  return 'unknown';
};

const isSlowNetwork = (browser?: AssistantBrowserCapabilities): boolean => {
  const effectiveType = browser?.effectiveType ?? 'unknown';
  if (effectiveType === 'slow-2g' || effectiveType === '2g' || effectiveType === '3g') {
    return true;
  }
  const downlink = asFinitePositive(browser?.downlinkMbps);
  if (downlink && downlink < LOCAL_VISION_MIN_DOWNLINK_MBPS) {
    return true;
  }
  return false;
};

const estimateColdStartMs = (browser?: AssistantBrowserCapabilities): number => {
  const downlinkMbps = asFinitePositive(browser?.downlinkMbps) ?? DEFAULT_DOWNLINK_MBPS;
  const downloadMs = (MOONDREAM2_Q4_DOWNLOAD_MB / (downlinkMbps / 8)) * 1000;
  const compileAndWarmupMs = 4500;
  return Math.round(downloadMs + compileAndWarmupMs);
};

export const resolveAssistantPreferredRuntime = (
  requested: AssistantPreferredRuntime | undefined,
  question: string | undefined
): AssistantPreferredRuntime => {
  if (requested === 'bedrock' || requested === 'browser_local') {
    return requested;
  }
  if (VISION_QUERY_PATTERN.test(question ?? '')) {
    return 'browser_local';
  }
  return 'auto';
};

export const collectBrowserCapabilities = (): AssistantBrowserCapabilities => {
  const nav = globalThis.navigator as (Navigator & {
    deviceMemory?: number;
    connection?: {
      downlink?: number;
      effectiveType?: string;
    };
    gpu?: unknown;
  }) | undefined;

  if (!nav) {
    return {
      webgpu: false,
      effectiveType: 'unknown',
    };
  }

  return {
    webgpu: Boolean(nav.gpu),
    deviceMemoryGb: asFinitePositive(nav.deviceMemory),
    downlinkMbps: asFinitePositive(nav.connection?.downlink),
    effectiveType: normalizeEffectiveType(nav.connection?.effectiveType),
    hardwareConcurrency: asFinitePositive(nav.hardwareConcurrency),
  };
};

const buildBedrockPlan = (input: {
  recommendedMode: AssistantExecutionMode;
  requiresOptIn: boolean;
  reason: string;
  fallbackReason: string | null;
  estimatedColdStartMs: number | null;
  policy?: AssistantRuntimePolicyHint;
}): AssistantExecutionPlan => ({
  selectedMode: 'bedrock',
  recommendedMode: input.recommendedMode,
  requiresOptIn: input.requiresOptIn,
  reason: input.reason,
  fallbackReason: input.fallbackReason,
  estimatedColdStartMs: input.estimatedColdStartMs,
  visualExtractorModel: input.policy?.multimodalGrounding?.visualExtractorModel ?? null,
  teacherModel: input.policy?.multimodalGrounding?.teacherModel ?? null,
});

export const resolveAssistantExecutionPlan = (
  input: ResolveAssistantExecutionPlanInput
): AssistantExecutionPlan => {
  const policy = input.policy;
  const preferences = input.preferences;
  const browser = preferences?.browser;
  const preferredRuntime = resolveAssistantPreferredRuntime(preferences?.preferredRuntime, input.question);
  const localVisionPreferred = preferredRuntime === 'browser_local';
  const localVisionEstimatedColdStartMs = estimateColdStartMs(browser);

  if (!localVisionPreferred) {
    return buildBedrockPlan({
      recommendedMode: 'bedrock',
      requiresOptIn: false,
      reason: 'bedrock_default_runtime',
      fallbackReason: null,
      estimatedColdStartMs: null,
      policy,
    });
  }

  if (policy?.executionMode === 'shadow') {
    return buildBedrockPlan({
      recommendedMode: 'browser_local',
      requiresOptIn: false,
      reason: 'runtime_policy_shadow_mode',
      fallbackReason: 'runtime_policy_shadow_mode',
      estimatedColdStartMs: localVisionEstimatedColdStartMs,
      policy,
    });
  }

  if (input.serverSupportsBrowserLocal === false) {
    return buildBedrockPlan({
      recommendedMode: 'browser_local',
      requiresOptIn: false,
      reason: 'server_runtime_does_not_host_browser_local',
      fallbackReason: 'server_runtime_no_browser_local',
      estimatedColdStartMs: localVisionEstimatedColdStartMs,
      policy,
    });
  }

  if (!browser?.webgpu) {
    return buildBedrockPlan({
      recommendedMode: 'browser_local',
      requiresOptIn: false,
      reason: 'browser_local_requires_webgpu',
      fallbackReason: 'webgpu_unavailable',
      estimatedColdStartMs: localVisionEstimatedColdStartMs,
      policy,
    });
  }

  if ((browser.deviceMemoryGb ?? 0) < LOCAL_VISION_MIN_DEVICE_MEMORY_GB) {
    return buildBedrockPlan({
      recommendedMode: 'browser_local',
      requiresOptIn: false,
      reason: 'browser_local_requires_more_memory',
      fallbackReason: 'insufficient_device_memory',
      estimatedColdStartMs: localVisionEstimatedColdStartMs,
      policy,
    });
  }

  if (isSlowNetwork(browser)) {
    return buildBedrockPlan({
      recommendedMode: 'browser_local',
      requiresOptIn: false,
      reason: 'browser_local_network_too_slow',
      fallbackReason: 'network_too_slow',
      estimatedColdStartMs: localVisionEstimatedColdStartMs,
      policy,
    });
  }

  return {
    selectedMode: 'browser_local',
    recommendedMode: 'browser_local',
    requiresOptIn: false,
    reason: 'browser_local_selected',
    fallbackReason: null,
    estimatedColdStartMs: localVisionEstimatedColdStartMs,
    visualExtractorModel: policy?.multimodalGrounding?.visualExtractorModel ?? 'moondream2',
    teacherModel: policy?.multimodalGrounding?.teacherModel ?? 'phi-3.5-mini',
  };
};
